import React, { useState, useEffect } from 'react';
import { Route, Switch, Redirect, useHistory } from "react-router-dom";
import { useAuth, useUser, useClerk } from '@clerk/react';
import { useApiClient } from '../services/api';
import { Routes } from "../routes";

// pages
import Upgrade from "./Upgrade";
import Users from "./users";
import UserDetails from "./UserDetails";
import AllExperiences from "./AllExperiences";
import PendingExperiences from "./PendingExperiences";
import Reports from "./Reports";
import HomeSections from "./HomeSections";
import BookDetails from "./BookDetails";
import Content from "./Content";
import ContentDetails from "./ContentDetails";
import BlockedBooks from "./BlockedBooks";
import FeaturedBooks from "./FeaturedBooks";
import BootstrapTables from "./tables/BootstrapTables";
import Signin from "./examples/Signin";


// documentation pages
import DocsOverview from "./documentation/DocsOverview";
import DocsDownload from "./documentation/DocsDownload";
import DocsQuickStart from "./documentation/DocsQuickStart";
import DocsLicense from "./documentation/DocsLicense";
import DocsFolderStructure from "./documentation/DocsFolderStructure";
import DocsBuild from "./documentation/DocsBuild";
import DocsChangelog from "./documentation/DocsChangelog";

// components
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Preloader from "../components/Preloader";

import Accordion from "./components/Accordion";
import Alerts from "./components/Alerts";
import Badges from "./components/Badges";
import Breadcrumbs from "./components/Breadcrumbs";
import Buttons from "./components/Buttons";
import Forms from "./components/Forms";
import Modals from "./components/Modals";
import Navs from "./components/Navs";
import Navbars from "./components/Navbars";
import Pagination from "./components/Pagination";
import Popovers from "./components/Popovers";
import Progress from "./components/Progress";
import Tables from "./components/Tables";
import Tabs from "./components/Tabs";
import Tooltips from "./components/Tooltips";
import Toasts from "./components/Toasts";

const RouteWithLoader = ({ component: Component, ...rest }) => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Route {...rest} render={props => ( <> <Preloader show={loaded ? false : true} /> <Component {...props} /> </> ) } />
  );
};

const PrivateRoute = ({ component: Component, ...rest }) => {
  const { isSignedIn, isLoaded } = useAuth();
  const { user, isLoaded: userLoaded } = useUser();
  const { signOut } = useClerk();
  const history = useHistory();
  const ADMIN_ROLES = ["admin", "super_admin"];
  const isAdmin = ADMIN_ROLES.includes(user?.publicMetadata?.role);

  // Non-admins never see the admin shell — bounce them back to the sign-in
  // screen (signed out) with an error, instead of stranding them mid-app.
  useEffect(() => {
    if (!isLoaded || !userLoaded) return;
    if (isSignedIn && !isAdmin) {
      signOut().then(() => history.replace(`${Routes.Signin.path}?denied=1`));
    }
  }, [isLoaded, userLoaded, isSignedIn, isAdmin, signOut, history]);

  if (!isLoaded || !userLoaded) return null;
  return (
    <Route
      {...rest}
      render={props => {
        if (!isSignedIn) return <Redirect to={Routes.Signin.path} />;
        if (!isAdmin) return null;
        return (
          <>
            <Sidebar />
            <main className="content">
              <Navbar />
              <Component {...props} />
              <Footer toggleSettings={() => {}} showSettings={true} />
            </main>
          </>
        );
      }}
    />
  );
};

const GuestRoute = ({ component: Component, ...rest }) => {
  const { isSignedIn, isLoaded } = useAuth();
  if (!isLoaded) return null;
  return (
    <Route
      {...rest}
      render={props =>
        isSignedIn ? (
          <Redirect to={Routes.Users.path} />
        ) : (
          <RouteWithLoader component={Component} {...props} />
        )
      }
    />
  );
};

export default () => {
  useApiClient();
  return (
  <Switch>
    {/* guest only */}
    <GuestRoute exact path={Routes.Signin.path} component={Signin} />

    {/* protected pages */}
    <PrivateRoute exact path={Routes.DashboardOverview.path} component={() => (<Redirect to={Routes.Users.path} />)} />
    <PrivateRoute exact path={Routes.Upgrade.path} component={Upgrade} />
    <PrivateRoute exact path={Routes.Users.path} component={Users} />
    <PrivateRoute exact path={Routes.UserDetails.path} component={UserDetails} />
    <PrivateRoute exact path={Routes.Experiences.path} component={AllExperiences} />
    <PrivateRoute
      exact
      path={Routes.ExperienceCreate.path}
      render={() => <PendingExperiences createOnly />}
    />
    <PrivateRoute
      exact
      path={Routes.PendingExperiences.path}
      render={() => <Redirect to={{ pathname: Routes.Experiences.path, search: "?state=pending" }} />}
    />
    <PrivateRoute
      exact
      path={Routes.ImportedExperiences.path}
      render={() => <Redirect to={{ pathname: Routes.Experiences.path, search: "?state=pending&importedOnly=1" }} />}
    />
    <PrivateRoute exact path={Routes.BookDetails.path} component={BookDetails} />
    <PrivateRoute exact path={Routes.Content.path} component={Content} />
    <PrivateRoute exact path={Routes.ContentDetails.path} component={ContentDetails} />
    <PrivateRoute exact path={Routes.Reports.path} component={Reports} />
    <PrivateRoute exact path={Routes.HomeSections.path} component={HomeSections} />
    <PrivateRoute exact path={Routes.BlockedBooks.path} component={BlockedBooks} />
    <PrivateRoute exact path={Routes.FeaturedBooks.path} component={FeaturedBooks} />
    <PrivateRoute exact path={Routes.BootstrapTables.path} component={BootstrapTables} />

    {/* components */}
    <PrivateRoute exact path={Routes.Accordions.path} component={Accordion} />
    <PrivateRoute exact path={Routes.Alerts.path} component={Alerts} />
    <PrivateRoute exact path={Routes.Badges.path} component={Badges} />
    <PrivateRoute exact path={Routes.Breadcrumbs.path} component={Breadcrumbs} />
    <PrivateRoute exact path={Routes.Buttons.path} component={Buttons} />
    <PrivateRoute exact path={Routes.Forms.path} component={Forms} />
    <PrivateRoute exact path={Routes.Modals.path} component={Modals} />
    <PrivateRoute exact path={Routes.Navs.path} component={Navs} />
    <PrivateRoute exact path={Routes.Navbars.path} component={Navbars} />
    <PrivateRoute exact path={Routes.Pagination.path} component={Pagination} />
    <PrivateRoute exact path={Routes.Popovers.path} component={Popovers} />
    <PrivateRoute exact path={Routes.Progress.path} component={Progress} />
    <PrivateRoute exact path={Routes.Tables.path} component={Tables} />
    <PrivateRoute exact path={Routes.Tabs.path} component={Tabs} />
    <PrivateRoute exact path={Routes.Tooltips.path} component={Tooltips} />
    <PrivateRoute exact path={Routes.Toasts.path} component={Toasts} />

    {/* documentation */}
    <PrivateRoute exact path={Routes.DocsOverview.path} component={DocsOverview} />
    <PrivateRoute exact path={Routes.DocsDownload.path} component={DocsDownload} />
    <PrivateRoute exact path={Routes.DocsQuickStart.path} component={DocsQuickStart} />
    <PrivateRoute exact path={Routes.DocsLicense.path} component={DocsLicense} />
    <PrivateRoute exact path={Routes.DocsFolderStructure.path} component={DocsFolderStructure} />
    <PrivateRoute exact path={Routes.DocsBuild.path} component={DocsBuild} />
    <PrivateRoute exact path={Routes.DocsChangelog.path} component={DocsChangelog} />

    {/* fallback */}
    <Redirect to={Routes.Users.path} />
  </Switch>
  );
};
